/**
 * useSetActiveVersion — the one client path to `setActiveVersionFn`: pin a
 * LIVE version as ACTIVE (rollback or roll-forward). Shared by the game
 * page's version-history table and the studio's Overview + Iterate tabs, so
 * the callable call, and its busy/error bookkeeping, live in exactly one
 * place. Callers own their own `useAttributedAction()` (and render its
 * modal) and pass `runAttributed` through, so a page with other attributed
 * actions (e.g. version delete) shares ONE attribution modal rather than
 * stacking one per hook.
 */
import { useState } from 'react';
import type { Creation } from '@djgg/creations';
import { useServices } from '../services/context.js';

type RunAttributed = (
  creation: Creation & { id: string },
  doAction: (attributeTo?: string) => Promise<void>,
) => Promise<void>;

export interface UseSetActiveVersion {
  /** Pin version `n` active. Ignored while another pin from this hook is in flight. */
  setActive: (n: number) => void;
  /** The version currently being pinned, or null when idle. */
  activatingVersion: number | null;
  error: string;
}

export function useSetActiveVersion(
  // Null while the creation is still loading (a caller mid-subscription, e.g.
  // the game page before its first snapshot) — setActive is then a no-op, so
  // the hook can be called unconditionally ahead of any early return.
  creation: (Creation & { id: string }) | null,
  runAttributed: RunAttributed,
): UseSetActiveVersion {
  const services = useServices();
  const [activatingVersion, setActivatingVersion] = useState<number | null>(null);
  const [error, setError] = useState('');

  const setActive = (n: number): void => {
    if (!creation || activatingVersion !== null) return;
    void runAttributed(creation, async (attributeTo) => {
      setError('');
      setActivatingVersion(n);
      try {
        await services.callable.setActiveVersion({ creationId: creation.id, version: n, attributeTo });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to set active version.');
      } finally {
        setActivatingVersion(null);
      }
    });
  };

  return { setActive, activatingVersion, error };
}
